import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { updateProfile } from '@/api/users';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'ui.theme';

function applyTheme(theme: Theme): void {
  const prefers = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  const resolved = theme === 'system' ? (prefers ? 'dark' : 'light') : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

/**
 * Theme hook. Reads the initial value from auth.user.theme (so server-side
 * preference wins when available) and falls back to localStorage.
 * `setTheme` writes to localStorage immediately (UI is responsive), applies
 * the class to <html>, and persists to the server via PATCH /users/me.
 */
export function useTheme(): { theme: Theme; setTheme: (next: Theme) => void; cycle: () => void } {
  const userTheme = useAuthStore((s) => s.user?.theme);
  const setUser = useAuthStore((s) => s.setUser);

  const [theme, setLocal] = useState<Theme>(() => {
    if (userTheme) return userTheme;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'system';
  });

  // Apply class on mount + whenever theme changes
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Re-apply when system preference changes (only matters for theme='system')
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  // If the user object arrives/changes after mount, sync (only if different)
  useEffect(() => {
    if (userTheme && userTheme !== theme) {
      setLocal(userTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTheme]);

  const setTheme = useCallback(
    (next: Theme) => {
      setLocal(next);
      // Optimistic: update store so subsequent reads are consistent
      const current = useAuthStore.getState().user;
      if (current) setUser({ ...current, theme: next });
      // Best-effort server sync; UI is already updated
      updateProfile({ theme: next }).then((u) => {
        setUser(u);
      }).catch(() => {
        // Swallow — local persistence is enough for offline / failed sync
      });
    },
    [setUser]
  );

  const cycle = useCallback(() => {
    const order: Theme[] = ['light', 'dark', 'system'];
    const idx = order.indexOf(theme);
    const next = order[(idx + 1) % order.length] ?? 'system';
    setTheme(next);
  }, [theme, setTheme]);

  return { theme, setTheme, cycle };
}
